/* Connection state.

   `navigator.onLine` is famously optimistic — it reports true for a captive
   portal or a connected-but-dead network. It is still the right signal for
   this banner: the failure mode is a banner that does not appear when it
   should, and the request-level `offline` error covers that case with a
   message the user can act on.

   The banner takes real layout space rather than floating over the header. An
   overlay would cover the back button on the exact screen where the user most
   wants to retreat. */

import { useEffect, useState } from 'react'
import { AnimatePresence, motion, useReducedMotion } from 'motion/react'
import { CloudOff } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { EASE_OUT } from '../../design/motion'

export function OfflineBanner() {
  const { t } = useTranslation()
  const reduce = useReducedMotion()
  const [offline, setOffline] = useState(() =>
    typeof navigator === 'undefined' ? false : !navigator.onLine,
  )

  useEffect(() => {
    const on = () => setOffline(false)
    const off = () => setOffline(true)
    window.addEventListener('online', on)
    window.addEventListener('offline', off)
    return () => {
      window.removeEventListener('online', on)
      window.removeEventListener('offline', off)
    }
  }, [])

  return (
    <AnimatePresence initial={false}>
      {offline && (
        <motion.div
          className="offline-banner"
          role="status"
          initial={reduce ? { opacity: 0 } : { height: 0, opacity: 0 }}
          animate={reduce ? { opacity: 1 } : { height: 'auto', opacity: 1 }}
          exit={reduce ? { opacity: 0 } : { height: 0, opacity: 0 }}
          transition={{ duration: 0.22, ease: EASE_OUT }}
        >
          <CloudOff size={15} />
          <span>{t('common.offline')}</span>
        </motion.div>
      )}
    </AnimatePresence>
  )
}
